/**
 * Shared model for the library CSV import report (backend
 * `CsvValidationReportDTO`, also served by the validate-only endpoint and
 * left on a failed job's registry row).
 *
 * The backend ships two channels of the same item shape
 * (`{ lineNumber, fieldName, errorType, message, details }`):
 *   - `errors`   — the file was rejected, nothing was imported;
 *   - `warnings` — things worth reading that never fail the import (a code
 *                  applied to a whole scope, a dropped predecessor link, a
 *                  deprecated column…).
 * plus per-object counters (`scopesCreated`, `blocksUpdated`, …) that make a
 * re-import verifiable: replaying an unmodified export reports 0 created and
 * 0 updated.
 *
 * Older backends send neither `warnings` nor the counters; everything here
 * degrades to "no warnings, no counters" rather than crashing.
 */
import { formatImportError } from "../ImportCSVDialog/formatImportError";

/** Object types the report can count, in display order. */
export const IMPORT_REPORT_OBJECTS = ["scopes", "blocks", "inputs", "tags"];
/** Counter kinds per object type, in display order. */
export const IMPORT_REPORT_KINDS = ["created", "updated", "unchanged"];

const isNumber = (v) => typeof v === "number" && Number.isFinite(v);

/**
 * Row number an issue points at, if any. The backend uses `lineNumber`;
 * other shapes reaching the dialogs use `line`.
 */
export const importIssueRow = (issue) => {
  if (!issue || typeof issue !== "object") return null;
  const row = issue.lineNumber ?? issue.line;
  return isNumber(row) || (typeof row === "string" && row !== "") ? row : null;
};

/** One issue as a single readable line ("Line 42 · Predecessors: …"). */
export const formatImportIssue = (issue, t) => formatImportError(issue, t);

/**
 * True when `value` looks like an import report: a structured object carrying
 * an `errors` or `warnings` list, or any of the per-object counters.
 */
export const isImportReport = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  if (Array.isArray(value.errors) || Array.isArray(value.warnings)) return true;
  return IMPORT_REPORT_OBJECTS.some((object) =>
    IMPORT_REPORT_KINDS.some((kind) => isNumber(value[counterKey(object, kind)]))
  );
};

/** DTO field name for a counter: ("scopes", "created") → "scopesCreated". */
export const counterKey = (object, kind) =>
  `${object}${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;

/**
 * Per-object counters present in the report. An object type is included only
 * when the backend sent at least one of its counters (missing kinds read 0),
 * so a legacy report yields `{}` and an inputs/tags-less report shows only
 * scopes and blocks.
 */
export const readImportCounters = (report) => {
  const counters = {};
  if (!report || typeof report !== "object") return counters;
  IMPORT_REPORT_OBJECTS.forEach((object) => {
    const present = IMPORT_REPORT_KINDS.filter((kind) => isNumber(report[counterKey(object, kind)]));
    if (present.length === 0) return;
    counters[object] = IMPORT_REPORT_KINDS.reduce((acc, kind) => {
      acc[kind] = isNumber(report[counterKey(object, kind)]) ? report[counterKey(object, kind)] : 0;
      return acc;
    }, {});
  });
  return counters;
};

/**
 * Normalizes any report-like value (new or legacy, or a bare `{ errors }`
 * error payload) into one predictable shape:
 *   { valid, dryRun, rowsParsed, scopeCount, blockCount,
 *     errors, errorCount, warnings, warningCount, counters, hasCounters }
 * Returns null for values that are not a report at all.
 */
export const normalizeImportReport = (raw) => {
  if (!isImportReport(raw)) return null;
  const errors = Array.isArray(raw.errors) ? raw.errors.filter((e) => e != null) : [];
  const warnings = Array.isArray(raw.warnings) ? raw.warnings.filter((w) => w != null) : [];
  const counters = readImportCounters(raw);
  const valid =
    typeof raw.valid === "boolean"
      ? raw.valid
      : typeof raw.success === "boolean"
        ? raw.success
        : errors.length === 0;
  return {
    valid,
    dryRun: raw.dryRun === true,
    rowsParsed: isNumber(raw.rowsParsed) ? raw.rowsParsed : null,
    scopeCount: isNumber(raw.scopeCount) ? raw.scopeCount : null,
    blockCount: isNumber(raw.blockCount) ? raw.blockCount : null,
    errors,
    errorCount: isNumber(raw.errorCount) ? Math.max(raw.errorCount, errors.length) : errors.length,
    warnings,
    warningCount: isNumber(raw.warningCount)
      ? Math.max(raw.warningCount, warnings.length)
      : warnings.length,
    counters,
    hasCounters: Object.keys(counters).length > 0,
  };
};

/** True when the report carries at least one warning. */
export const hasImportWarnings = (report) => {
  const normalized = normalizeImportReport(report);
  return !!normalized && normalized.warnings.length > 0;
};

/**
 * Pulls a report out of an async-job status row or terminal frame. The
 * backend leaves a failed job's report as JSON in `error`; a completed job
 * may carry it under `result` (or `report`) — either as an object or as a
 * JSON string. Returns null when nothing report-shaped is there.
 */
export const extractImportReport = (source) => {
  if (!source || typeof source !== "object") return null;
  const candidates = [source.report, source.result, source.error];
  for (const candidate of candidates) {
    if (isImportReport(candidate)) return candidate;
    if (typeof candidate === "string" && candidate.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(candidate);
        if (isImportReport(parsed)) return parsed;
      } catch {
        // not JSON — plain error text
      }
    }
  }
  return null;
};
